import {SortingState} from '@tanstack/table-core';
import {snakeCase} from 'lodash';
import {useCallback} from 'react';
import {z} from 'zod';
import mapKeysToCase from '@/utils/mapKeysToCase';
import translate from '@/utils/translate';
import {
  type CreatorBrandSafetyVetting,
  type VettingsTabCounts,
} from '@/types/creatorBrandSafetyVetting';
import {VettingsPageSchema, type VettingsPage} from '@/types/vettingsPage';
import vettingHubFetchers from '@/api/fetchers/vettingHub';
import getFailureReasonTooltip from '@/views/vetting/vetting-hub/utils/failureReasonTooltip';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';

export const queryKey = 'creatorBrandSafetyVettings';

function mapVettingResult(vettingResult: CreatorBrandSafetyVetting) {
  const isUnavailable = !!vettingResult.optedOut;
  const status = isUnavailable ? 'vetting_unavailable' : vettingResult.status;
  let statusTooltip = '';
  if (isUnavailable) {
    statusTooltip = translate(
      "This creator enabled privacy settings that don't allow vetting"
    ) as string;
  } else if (vettingResult.postCount === 0) {
    statusTooltip = translate('No posts found for this creator') as string;
  } else if (vettingResult.status === 'report_failed') {
    statusTooltip = getFailureReasonTooltip(vettingResult.failureReason);
  }
  return {
    ...vettingResult,
    status,
    statusTooltip,
  };
}

type UseCreatorBrandSafetyVettingsQueryParams = {
  brandId: string;
  enabled?: boolean;
  tab?: string;
  sorting?: SortingState;
  profileId?: string;
  search?: string;
  scope?: 'brand' | 'agency';
};

function useCreatorBrandSafetyVettingsQuery({
  brandId,
  enabled = true,
  tab,
  sorting,
  profileId,
  search,
  scope,
}: UseCreatorBrandSafetyVettingsQueryParams) {
  const response = useInfiniteReactQuery({
    queryKey: [
      queryKey,
      brandId,
      tab,
      sorting?.map((sort) => `${sort.id}-${sort.desc}`),
      profileId,
      search,
      scope,
    ],
    queryFn: vettingHubFetchers.creatorBrandSafetyVettings,
    enabled,
    meta: {brandId, tab, sorting, profileId, search, scope},
    cacheTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
    getNextPageParam: (lastPage: any) => {
      const pagination = lastPage?.meta?.pagination;
      if (!pagination) return undefined;

      const {currentPage, totalPages} = pagination;
      if (currentPage >= totalPages) return undefined;

      return currentPage + 1;
    },
  });

  const {pages = []} = response?.data || {pages: []};
  const parsedPages = z.array(VettingsPageSchema).safeParse(pages);
  const typedPages: VettingsPage[] = parsedPages.success
    ? parsedPages.data
    : [];

  const vettingResults =
    typedPages.length === 0
      ? undefined
      : typedPages.flatMap((page) => page.data).map(mapVettingResult);

  const [firstPage] = typedPages;
  const rawTabCounts = firstPage?.meta?.tabCounts;
  const tabCounts: VettingsTabCounts | undefined = rawTabCounts
    ? (mapKeysToCase(rawTabCounts, snakeCase) as unknown as VettingsTabCounts)
    : undefined;

  const totalCount: number | undefined = firstPage?.meta?.totalCount;

  const {hasNextPage, fetchNextPage: fetchNext} = response;
  const fetchNextPage = useCallback((): Promise<void> => {
    if (!hasNextPage) return Promise.resolve();
    return fetchNext().then(() => {});
  }, [hasNextPage, fetchNext]);

  return {
    ...response,
    data: vettingResults,
    fetchNextPage,
    tabCounts,
    totalCount,
  };
}

export default useCreatorBrandSafetyVettingsQuery;
